// ============================================================
//  ALTIMO Medical Center — BuscadorGlobal.jsx  (v1)
//  Buscador único: pacientes + admisiones + facturas
//  Ubicación: src/components/BuscadorGlobal.jsx
//
//  Uso: se monta una sola vez en App.jsx. Se abre con Cmd/Ctrl+K
//  desde cualquier módulo. Es 100% aditivo — no reemplaza ningún
//  buscador existente en Facturación, Cuenta del Paciente,
//  Formularios o Expedientes; esos siguen funcionando igual.
//
//  Respeta PERMISOS: solo muestra resultados de facturas si el
//  rol del usuario tiene acceso a 'facturacion' o 'cuenta', y
//  solo navega a módulos a los que el usuario tiene permiso.
// ============================================================

import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../apiClient';

const S = {
  overlay: {
    position:'fixed', inset:0, background:'rgba(13,20,45,.55)',
    display:'flex', alignItems:'flex-start', justifyContent:'center',
    paddingTop:'10vh', zIndex:5000,
  },
  panel: {
    width:'600px', maxWidth:'92vw', maxHeight:'70vh',
    background:'#fff', borderRadius:'14px', overflow:'hidden',
    boxShadow:'0 24px 60px rgba(0,0,0,.28)',
    display:'flex', flexDirection:'column',
    fontFamily:'Segoe UI, sans-serif',
  },
  inputBar: {
    display:'flex', alignItems:'center', gap:'10px',
    padding:'14px 18px', borderBottom:'1px solid #eef1f6',
  },
  input: {
    flex:1, border:'none', outline:'none', fontSize:'15px',
    color:'#1B2A5E', background:'transparent',
  },
  hint: {
    fontSize:'10px', color:'#adb5bd', border:'1px solid #dde3ea',
    borderRadius:'5px', padding:'2px 6px', flexShrink:0,
  },
  body: { flex:1, overflowY:'auto', padding:'6px 0' },
  grupoLabel: {
    fontSize:'10px', fontWeight:'800', color:'#7fa3cc', letterSpacing:'.08em',
    textTransform:'uppercase', padding:'10px 18px 4px',
  },
  item: (activo) => ({
    display:'flex', alignItems:'center', gap:'10px',
    padding:'9px 18px', cursor:'pointer',
    background: activo ? '#e8f0fb' : 'transparent',
  }),
  itemIco: {
    width:'30px', height:'30px', borderRadius:'8px', flexShrink:0,
    display:'flex', alignItems:'center', justifyContent:'center',
    fontSize:'14px', background:'#f0f4f8',
  },
  itemTitulo: { fontSize:'13px', fontWeight:'700', color:'#1B2A5E' },
  itemSub: { fontSize:'11px', color:'#6c757d', marginTop:'1px' },
  badgeEstado: (color) => ({
    fontSize:'10px', fontWeight:'700', padding:'2px 8px', borderRadius:'99px',
    background: color || '#e8f0fb', color:'#1B2A5E', marginLeft:'auto', flexShrink:0,
  }),
  vacio: { padding:'30px 18px', textAlign:'center', color:'#6c757d', fontSize:'13px' },
  footer: {
    borderTop:'1px solid #eef1f6', padding:'8px 18px',
    fontSize:'10px', color:'#adb5bd', display:'flex', gap:'14px',
  },
};

const ESTADO_COLOR = {
  Activa: '#d4edda', Alta: '#e2e3e5', Pagada: '#d4edda',
  Pendiente: '#fff3cd', Anulada: '#f8d7da',
};

function debeVerFacturas(usuario, PERMISOS) {
  if (!usuario) return false;
  return PERMISOS.facturacion?.includes(usuario.rol) || PERMISOS.cuenta?.includes(usuario.rol);
}
function debeVerExpedientes(usuario, PERMISOS) {
  if (!usuario) return false;
  return PERMISOS.expediente?.includes(usuario.rol);
}

export default function BuscadorGlobal({ usuario, PERMISOS, abierto, onCerrar, onIrACuenta, onIrAFacturacion, onIrAModulo }) {
  const [texto,      setTexto]      = useState('');
  const [resultados, setResultados] = useState({ pacientes:[], admisiones:[], facturas:[] });
  const [buscando,   setBuscando]   = useState(false);
  const [activoIdx,  setActivoIdx]  = useState(0);
  const inputRef = useRef(null);

  // Lista plana para navegar con teclado (↑↓ + Enter)
  const verFacturas    = debeVerFacturas(usuario, PERMISOS);
  const verExpedientes = debeVerExpedientes(usuario, PERMISOS);

  const planos = [
    ...resultados.pacientes.map(p   => ({ tipo:'paciente',  data:p })),
    ...resultados.admisiones.map(a  => ({ tipo:'admision',  data:a })),
    ...(verFacturas ? resultados.facturas.map(f => ({ tipo:'factura', data:f })) : []),
  ];

  // Foco automático al abrir + reset de estado
  useEffect(() => {
    if (abierto) {
      setTexto(''); setResultados({ pacientes:[], admisiones:[], facturas:[] }); setActivoIdx(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [abierto]);

  // Debounce de búsqueda (mismo patrón 350ms usado en Facturación/CuentaPaciente)
  useEffect(() => {
    if (!abierto) return;
    if (texto.trim().length < 2) { setResultados({ pacientes:[], admisiones:[], facturas:[] }); return; }
    const t = setTimeout(async () => {
      setBuscando(true);
      try {
        const r = await api.global.buscar(texto.trim());
        if (r.ok) { setResultados(r.data); setActivoIdx(0); }
      } finally {
        setBuscando(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [texto, abierto]);

  const seleccionar = useCallback((entrada) => {
    if (!entrada) return;
    onCerrar();
    if (entrada.tipo === 'paciente') {
      // Si tiene admisión activa y el rol tiene acceso a cuenta, ir directo;
      // si no, ir al módulo de expedientes (sin romper su flujo propio de búsqueda).
      if (entrada.data.admision_estado === 'Activa' && entrada.data.ultima_admision_id && PERMISOS.cuenta?.includes(usuario.rol)) {
        onIrACuenta(entrada.data.ultima_admision_id);
      } else if (verExpedientes) {
        onIrAModulo('expediente');
      }
    } else if (entrada.tipo === 'admision') {
      onIrACuenta(entrada.data.id);
    } else if (entrada.tipo === 'factura') {
      onIrAFacturacion(entrada.data.admision_id);
    }
  }, [usuario, PERMISOS, verExpedientes, onCerrar, onIrACuenta, onIrAFacturacion, onIrAModulo]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { onCerrar(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActivoIdx(i => Math.min(i + 1, planos.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setActivoIdx(i => Math.max(i - 1, 0)); }
    if (e.key === 'Enter')     { e.preventDefault(); seleccionar(planos[activoIdx]); }
  };

  if (!abierto) return null;

  const fmtFecha = (f) => {
    if (!f) return '';
    try { return new Date(String(f).replace(' ', 'T')).toLocaleDateString('es-SV'); }
    catch (_) { return f; }
  };

  let idxGlobal = -1;

  return (
    <div style={S.overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onCerrar(); }}>
      <div style={S.panel} onMouseDown={(e) => e.stopPropagation()}>
        <div style={S.inputBar}>
          <span style={{ fontSize:'16px' }}>🔍</span>
          <input
            ref={inputRef}
            style={S.input}
            placeholder="Buscar paciente, admisión o factura..."
            value={texto}
            onChange={e => setTexto(e.target.value)}
            onKeyDown={onKeyDown}
          />
          {buscando && <span style={{ fontSize:'11px', color:'#6c757d' }}>Buscando...</span>}
          <span style={S.hint}>Esc</span>
        </div>

        <div style={S.body}>
          {texto.trim().length < 2 && (
            <div style={S.vacio}>Escribe al menos 2 caracteres — nombre, DUI, código, # de expediente o # de factura.</div>
          )}

          {texto.trim().length >= 2 && !buscando && planos.length === 0 && (
            <div style={S.vacio}>Sin resultados para "{texto.trim()}".</div>
          )}

          {resultados.pacientes.length > 0 && (
            <>
              <div style={S.grupoLabel}>Pacientes</div>
              {resultados.pacientes.map(p => {
                idxGlobal++;
                const i = idxGlobal;
                return (
                  <div key={`p${p.id}`} style={S.item(i === activoIdx)}
                    onMouseEnter={() => setActivoIdx(i)}
                    onClick={() => seleccionar({ tipo:'paciente', data:p })}>
                    <div style={S.itemIco}>🧑</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={S.itemTitulo}>{p.nombre} {p.apellido}</div>
                      <div style={S.itemSub}>
                        {p.codigo ? `Cód. ${p.codigo}` : ''}{p.dui ? ` · DUI ${p.dui}` : ''}
                      </div>
                    </div>
                    {p.admision_estado && (
                      <span style={S.badgeEstado(ESTADO_COLOR[p.admision_estado])}>{p.admision_estado}</span>
                    )}
                  </div>
                );
              })}
            </>
          )}

          {resultados.admisiones.length > 0 && (
            <>
              <div style={S.grupoLabel}>Admisiones / Expedientes</div>
              {resultados.admisiones.map(a => {
                idxGlobal++;
                const i = idxGlobal;
                return (
                  <div key={`a${a.id}`} style={S.item(i === activoIdx)}
                    onMouseEnter={() => setActivoIdx(i)}
                    onClick={() => seleccionar({ tipo:'admision', data:a })}>
                    <div style={S.itemIco}>📂</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={S.itemTitulo}>Expediente #{a.id} — {a.paciente}</div>
                      <div style={S.itemSub}>
                        {fmtFecha(a.fecha_ingreso)}{a.habitacion ? ` · Hab. ${a.habitacion}` : ''}
                      </div>
                    </div>
                    <span style={S.badgeEstado(ESTADO_COLOR[a.estado])}>{a.estado}</span>
                  </div>
                );
              })}
            </>
          )}

          {verFacturas && resultados.facturas.length > 0 && (
            <>
              <div style={S.grupoLabel}>Facturas</div>
              {resultados.facturas.map(f => {
                idxGlobal++;
                const i = idxGlobal;
                return (
                  <div key={`f${f.id}`} style={S.item(i === activoIdx)}
                    onMouseEnter={() => setActivoIdx(i)}
                    onClick={() => seleccionar({ tipo:'factura', data:f })}>
                    <div style={S.itemIco}>🧾</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={S.itemTitulo}>Factura {f.numero} — {f.paciente}</div>
                      <div style={S.itemSub}>
                        {fmtFecha(f.emitida_en)} · ${Number(f.total || 0).toFixed(2)}
                      </div>
                    </div>
                    <span style={S.badgeEstado(ESTADO_COLOR[f.estado])}>{f.estado}</span>
                  </div>
                );
              })}
            </>
          )}
        </div>

        <div style={S.footer}>
          <span>↑↓ navegar</span>
          <span>↵ seleccionar</span>
          <span>Esc cerrar</span>
        </div>
      </div>
    </div>
  );
}
